import { businessApiRoute, get as adminGet, getBusinessId } from './Api';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import * as Toast from '../lib/Toast.js';
import * as ClientApi from '~/lib/ClientApi';
import { useClientLogin } from '~/lib/useClientLogin';

async function sharedGet(route) {
    if (getBusinessId()) {
        return await adminGet(businessApiRoute(`${route}`));
    } else {
        return await axios
            .get(`/api${route}`)
            .then(res => res.data)
            .catch(err => {
                if (err?.request?.status === 429) {
                    Toast.dismiss(); // This can be triggered alot
                    Toast.error(
                        'There are too many requests at this time. Please wait a minute, then refresh the page and try again.',
                    );
                }

                throw err;
            });
    }
}

export function useEventTypesQuery(bookableResource, filterBookableResources) {
    bookableResource = parseInt(bookableResource);
    filterBookableResources = (filterBookableResources || [])
        .map(id => parseInt(id))
        .filter(id => !!id);

    const { isLoggedIn } = useClientLogin();
    //const clientToken = ClientApi.useClientToken();
    //console.log('clientToken=', clientToken);

    return useQuery({
        queryKey: [
            getBusinessId(),
            'shared-event-types',
            bookableResource,
            JSON.stringify(filterBookableResources),
            isLoggedIn,
        ],
        queryFn: async () => {
            let res = null;
            // If we're logged in we want any context from the client
            if (isLoggedIn) {
                res = await ClientApi.get(
                    `/scheduling/event-types?bookableResource=${bookableResource}`,
                );
            } else {
                res = await sharedGet(
                    `/scheduling/event-types?bookableResource=${bookableResource}`,
                );
            }

            // Filter if needed...
            if (filterBookableResources.length > 0) {
                // Make sure if we have filters that this is part of it...
                return res.filter(et => {
                    return !!et.bookable_resources?.find(br =>
                        filterBookableResources.includes(parseInt(br.id)),
                    );
                });
            }
            return res;
        },
        staleTime: import.meta.env.DEV ? undefined : 1000 * 30,
    });
}

export function useBookableResourcesQuery(
    eventTypeId,
    startTime,
    filterEventTypes,
) {
    eventTypeId = parseInt(eventTypeId);

    filterEventTypes = (filterEventTypes || [])
        .map(id => parseInt(id))
        .filter(id => !!id);

    return useQuery({
        queryKey: [
            getBusinessId(),
            'bookable-resources',
            eventTypeId,
            JSON.stringify(startTime),
            JSON.stringify(filterEventTypes),
        ],
        queryFn: async () => {
            const res = await sharedGet(
                `/scheduling/bookable-resources?eventType=${eventTypeId}${startTime ? `&startTime=${encodeURIComponent(startTime)}` : ''}`,
            );

            if (filterEventTypes.length > 0) {
                // Make sure if we have filters that this is part of it...
                return res.filter(br => {
                    return !!br.event_types?.find(et =>
                        filterEventTypes.includes(parseInt(et.id)),
                    );
                });
            }
            return res;
        },
        staleTime: 1000 * 30,
    });
}

export function useAvailabilityQuery(
    eventTypeId,
    bookableResourceId,
    start,
    end,
    clientTz,
    opts,
) {
    eventTypeId = parseInt(eventTypeId);
    bookableResourceId = '' + bookableResourceId;

    return useQuery({
        queryKey: [
            getBusinessId(),
            'availability',
            eventTypeId,
            bookableResourceId,
            start,
            end,
            clientTz,
            opts,
        ],
        queryFn: async () => {
            return await sharedGet(
                `/scheduling/availability?eventType=${eventTypeId}&bookableResource=${bookableResourceId}&start=${start.toISOString()}&end=${end.toISOString()}&timezone=${clientTz}&ignoreEvents=${opts?.ignoreEvents ? JSON.stringify(opts.ignoreEvents) : ''}&extraTime=${opts?.extraTime ? opts.extraTime : ''}`,
            );
        },
    });
}

export function useSeriesAvailabilityQuery(
    eventTypeId,
    bookableResourceId,
    clientTz,
    opts,
) {
    eventTypeId = parseInt(eventTypeId);
    bookableResourceId = parseInt(bookableResourceId);

    return useQuery({
        queryKey: [
            getBusinessId(),
            'availability',
            eventTypeId,
            bookableResourceId,
            clientTz,
            opts,
        ],
        queryFn: async () => {
            return await sharedGet(
                `/scheduling/series-availability?eventType=${eventTypeId}&bookableResource=${bookableResourceId}&timezone=${clientTz}`,
            );
        },
    });
}

export function useClassAvailabilityQuery(start, end, clientTz, opts) {
    return useQuery({
        queryKey: [
            getBusinessId(),
            'class-availability',
            start,
            end,
            clientTz,
            JSON.stringify(opts),
        ],
        queryFn: async () => {
            return await sharedGet(
                `/scheduling/class-availability?start=${start.toISOString()}&end=${end.toISOString()}&timezone=${clientTz}&opts=${encodeURIComponent(JSON.stringify(opts))}&ignoreEvents=${opts?.ignoreEvents ? JSON.stringify(opts.ignoreEvents) : ''}`,
            );
        },
    });
}

export function usePublicPackagesQuery(opts) {
    return useQuery({
        queryKey: [getBusinessId(), 'client-packages'],
        queryFn: async () => {
            return await sharedGet(`/public-packages`);
        },
    });
}

export function useOverviewQuery(start, end, bookableResources) {
    return useQuery({
        queryKey: [
            getBusinessId(),
            'scheduling-overview',
            start,
            end,
            JSON.stringify(bookableResources),
        ],
        queryFn: async () => {
            const bookableResourceIdsParam = bookableResources
                .map(br => `bookableResources[]=${br.id}`)
                .join('&');

            const res = await sharedGet(
                `/scheduling/monthly-overview?start=${start.toISOString()}&end=${end.toISOString()}&${bookableResourceIdsParam}`,
            );

            return res;
        },
        //staleTime: 1000 * 30,
    });
}
